import _ from 'lodash';
import { DateTime } from 'luxon';

import { EventForm } from '../components/FuelCalendarContainer';
import icsToJson from '../components/FuelCalendarContainer/icsToJson';
import { toIcsString } from '../components/FuelCalendarContainer/toIcsString';
import { FuelEntityType } from '../services';
import clientUtils from './clientUtils';
import { compressionUtils } from './compressionUtils';
import { dateUtils } from './dateUtils';

const calendarUtils = {
  /** Gets ics string from events */
  getICSString(newEvents: EventForm[], timezone: string) {
    let icsString = toIcsString(newEvents, timezone);
    icsString = calendarUtils.cleanseICSString(icsString);
    return icsString;
  },

  /** Returns all events without the live program id given */
  removeEPGEventsByLiveProgramId(events: EventForm[], liveProgramId: string): EventForm[] {
    // console.log('removeEPGEventsByLiveProgramId', { events, liveProgramId });
    const newEvents: EventForm[] = [];
    if (events.length > 0) {
      for (let i = 0; i < events.length; i++) {
        const event = events[i];
        let toRemove = false;
        const fuelEntityType = event.attachedFuelEntityType;
        const fuelEntityId = event.attachedFuelEntityId;
        if (fuelEntityType === FuelEntityType.LiveProgram) {
          const id = clientUtils.parseFuelEntityId(fuelEntityId);
          if (id && id == liveProgramId) {
            toRemove = true;
          }
        }
        if (!toRemove) {
          newEvents.push(event);
        }
      }
    }
    return newEvents;
  },

  getLiveProgramIdsFromEvents(events: EventForm[], daysToKeep = 7): string[] {
    let liveProgramIds: string[] = [];
    if (events.length > 0) {
      for (let i = 0; i < events.length; i++) {
        const event = events[i];
        if (EventForm.checkEvent(event, daysToKeep)) {
          const fuelEntityType = event.attachedFuelEntityType;
          const fuelEntityId = event.attachedFuelEntityId;
          if (fuelEntityType === FuelEntityType.LiveProgram) {
            const id = clientUtils.parseFuelEntityId(fuelEntityId);
            if (id) {
              liveProgramIds.push(id);
            }
          }
        }
      }
    }
    liveProgramIds = _.uniq(liveProgramIds);
    return liveProgramIds;
  },

  getEventsFromICS(icsString: string, timezone, daysToKeep = 7): EventForm[] {
    const newEvents: EventForm[] = [];
    const icsToJsonEvents = icsToJson(icsString);
    // console.log('getEventsFromICS', icsToJsonEvents);
    for (let i = 0; i < icsToJsonEvents.length; i++) {
      const newEvent = EventForm.convertIcsJsonToEventForm(icsToJsonEvents[i], timezone);
      // aalt 2023-08-18 logic to cleanse older dates out of the ics
      if (EventForm.checkEvent(newEvent, daysToKeep)) {
        newEvents.push(newEvent);
      }
    }
    return newEvents;
  },

  populateICSEvent(event: any): any {
    const eventIn = _.cloneDeep(event);
    // console.log('populateICSEvent', { eventIn });

    // TODO: Issue with empty keys and multiline description
    // ASMR for Relaxation
    // Euphoria-inducing Content
    // ASMR From Restflix

    Object.keys(event).forEach((k) => {
      // console.log('key: ' + k + ', value: ' + event[k]);
      // console.log('key is truthy: ', !!k);
      if (k === 'Description' && event[k] != null) {
        // console.log('cleanseICSEvent', k, event[k]);
        // const originalDescription = ;
        const newDescription = event[k].replace(/\r\n\t/g, '\n').replace(/\\n/g, '\n');
        event[k] = newDescription;
      }
    });

    // console.log('populateICSEvent', { event });
    return event;
  },

  cleanseICSEvent(event: any): any {
    const eventIn = _.cloneDeep(event);
    // console.log('cleanseICSEvent', { eventIn });

    // TODO: Issue with empty keys and multiline description
    // ASMR for Relaxation
    // Euphoria-inducing Content
    // ASMR From Restflix

    Object.keys(event).forEach((k) => {
      // console.log('key: ' + k + ', value: ' + event[k]);
      // console.log('key is truthy: ', !!k);
      if (k === 'description' && event[k] != null) {
        // console.log('cleanseICSEvent', event[k]);
        const originalDescription = _.cloneDeep(event[k]);
        // console.log('originalDescription', originalDescription)
        const newDescription = originalDescription.replace(/\r\n\t/g, '\n').replace(/\n/g, '\r\n\t');
        event[k] = newDescription;
        // console.log('newDescription', newDescription)
      }
      k === '' && delete event[k];
    });

    // console.log('ICS EVENT cleansed', { event });
    return event;
  },

  /**
   * decodeICalendarConfig
   * @param iCalendarConfig The endcoded icsString saved in entity
   * @param isCompressed
   * @returns An uncompressed string in ics format to import into your calendar object eg. scheduleObj.importICalendar(decodedICalendarConfig)
   */
  decodeICalendarConfig(iCalendarConfig: string, isCompressed: boolean): string {
    // console.log('decodeICalendarConfig', { iCalendarConfig, isCompressed });
    if (!iCalendarConfig) return null;
    let decodedIcalString: string = '';
    if (isCompressed) {
      decodedIcalString = compressionUtils.decompress(iCalendarConfig);
    } else {
      decodedIcalString = compressionUtils.base64Decode(iCalendarConfig);
    }
    decodedIcalString = calendarUtils.prepICSStringForSchedulerImport(decodedIcalString);
    return decodedIcalString;
  },

  prepICSStringForSchedulerImport(icsString: string): string {
    if (icsString && icsString.length > 0) {
      // console.log('prepICSStringForSchedulerImport', { icsString });
      icsString = icsString.replace(/\r\n\t/g, '\\n');
      // console.log('prepICSStringForSchedulerImport return', { icsString });
    }
    return icsString;
  },

  cleanseICSString(icsString: string): string {
    // console.log('cleanseICSString', { icsString });
    // DESCRIPTION;ENCODING=QUOTED-PRINTABLE:
    // icsString = icsString
    //   .replace(/\r\nDESCRIPTION:/g, '\r\nDESCRIPTION;ENCODING=QUOTED-PRINTABLE:')
    //   //   .replace(/<<NEWLINE>>/g, '=0D=0A');
    //   .replace(/<<NEWLINE>>/g, '\\n');

    // Issue with empty lines with only a colon (:) on them
    // icsString = icsString.replace(/Categories:\r\n/g, 'Categories:\r\n:\r\n');
    // console.warn('IMPORTANT: icsString with error', { icsString });
    icsString = icsString.replace(/\r\n:\s?\r\n/g, '\r\n');
    // console.warn('IMPORTANT: icsString cleansed', { icsString });
    return icsString;
  },

  getCleansedICSString(scheduleObj: any): string {
    if (scheduleObj) {
      // console.log('getCleansedICSString', scheduleObj.eventsData.length);
      for (let i = 0; i < scheduleObj.eventsData.length; i++) {
        let event = scheduleObj.eventsData[i];
        event = calendarUtils.cleanseICSEvent(event);
      }
      const icsString = scheduleObj.iCalendarExportModule.getCalendarString();
      // console.log('getCleansedICSString', icsString);
      const icsStringCleansed = calendarUtils.cleanseICSString(icsString);
      return icsStringCleansed;
    }
  },

  getDuration(start, end): string {
    const seconds = dateUtils.getDifferenceDurationByUnit(end, start);
    return clientUtils.formatDuration(seconds);
  },
  getEventColor(attachedFuelEntityType, sourceType: string = ''): string {
    let color = '';
    if (attachedFuelEntityType === 'LiveProgram') {
      color = '#ff4147';
    } else if (attachedFuelEntityType === 'NestedChannel') {
      color = '#8a8a8a';
    } else if (attachedFuelEntityType === 'AdBreak') {
      color = '#f0ad4e';
    } else if (attachedFuelEntityType === 'Clip' && sourceType === 'ClipGroup') {
      color = '#0db080';
    }
    return color;
  },
  setOtherZone(dateString, timezone) {
    // console.log({dateString})
    const dt1 = DateTime.fromISO(dateString); // in browser default time zone, e.g. America/Denver
    const dt2 = DateTime.fromISO(dateString).setZone(timezone); // in current state time zone e.g. Africa/Bamako
    let modifiedDate = DateTime.fromISO(dateString);

    modifiedDate = modifiedDate.minus({ minutes: dt1.offset - dt2.offset });

    return modifiedDate.toJSDate();
  },
  /**
   * This function is needed to convert the ics date to the current ou timezone.
   * NOTE: DST shift will convert March 12, 2am PST automatically to 3am PST (eg. 2023-03-12T02:00:00 -> 2023-03-12T03:00:00)
   * This behavior is intended via timezone specs for conversion and daylight shift.
   * @param date the icalNet date string eg. 2023-03-19T02:00:00
   * @param dateTimezone ics TZID as iana timezone eg. "America/New_York"
   * @param timezone current ou timezone as iana timezone eg. "America/Los_Angeles"
   * @returns A date with the same format in the given timezone
   */
  rezoneICalDate(date, dateTimezone, timezone) {
    // NOTE: DST shift will convert March 12, 2am PST automatically to 3am PST (eg. 2023-03-12T02:00:00 -> 2023-03-12T03:00:00)
    // This behavior is intended via timezone specs for conversion and daylight shift.
    // if (dateTimezone === timezone) return date;
    // 2023-03-19T02:00:00
    // aalt always force the timezone conversion
    const year = date.substring(0, 4);
    const month = date.substring(5, 7);
    const day = date.substring(8, 10);
    const hour = date.substring(11, 13);
    const minute = date.substring(14, 16);
    const second = date.substring(17, 19);
    return DateTime.fromObject({ year, month, day, hour, minute, second }, { zone: dateTimezone })
      .setZone(timezone)
      .toFormat("yyyy-MM-dd'T'HH:mm:ss");
  },
};

export default calendarUtils;
